import { cva, type VariantProps } from 'class-variance-authority';
import { focusRing, transitionBase, uiMarker, uiMarkerLayout } from './shared';

/**
 * PLAN CARD
 *
 * A subscription plan on the sign-up flow: name, price line, what is
 * included, and the button that picks it. The page drew it twice — once as
 * the single recommended plan, open and with a countdown in the corner, and
 * once per row of the "all plans" list, folded shut behind a toggle with a
 * "Recommended" tag floating on the top edge of the one it had suggested.
 *
 * `emphasis` is the primary border that marks the recommendation in both
 * places. `size` is the difference between the two: the single card is
 * larger and rounder than a row in a list.
 */
export const planCardRecipe = cva(
  [uiMarker, 'relative border-2 bg-card', transitionBase],
  {
    variants: {
      emphasis: {
        true: 'border-primary',
        false: 'border-border',
      },
      size: {
        md: 'rounded-xl',
        lg: 'rounded-2xl',
      },
    },
    defaultVariants: { emphasis: false, size: 'md' },
  }
);

/** The always-open body: the recommended card's padding. */
export const planCardStaticRecipe = cva(
  [uiMarker, uiMarkerLayout, 'flex flex-col gap-4'],
  {
    variants: {
      size: {
        md: 'p-5',
        lg: 'p-6',
      },
    },
    defaultVariants: { size: 'md' },
  }
);

/**
 * The head of a folding card. A button rather than a Button: it is the whole
 * top of the card, padded to its edges, and takes the card's corners so the
 * hover tint does not poke out.
 */
export const planCardHeadRecipe = cva(
  [
    uiMarker,
    'flex w-full items-center justify-between gap-4 p-5 text-left hover:bg-muted/30',
    transitionBase,
    focusRing,
  ],
  {
    variants: {
      size: {
        md: 'rounded-xl',
        lg: 'rounded-2xl',
      },
    },
    defaultVariants: { size: 'md' },
  }
);

/** The chevron on a folding head, turned over when open. */
export const planCardChevronRecipe = cva(
  [uiMarker, 'text-muted-foreground transition-transform duration-500'],
  {
    variants: {
      expanded: { true: 'rotate-180', false: '' },
    },
    defaultVariants: { expanded: false },
  }
);

/**
 * The folding body. Height and opacity rather than mount/unmount, so the
 * open animation is a slide and the closed content still exists for the
 * next toggle to reveal.
 */
export const planCardBodyRecipe = cva(
  [uiMarker, uiMarkerLayout, 'overflow-hidden transition-all duration-500'],
  {
    variants: {
      expanded: {
        true: 'max-h-96 opacity-100',
        false: 'max-h-0 opacity-0',
      },
    },
    defaultVariants: { expanded: false },
  }
);

/** The tag floating on the top edge — "Recommended". */
export const planCardBadgeRecipe = cva([
  uiMarker,
  'absolute -top-2 right-4 rounded-pill bg-primary px-3 py-1 font-sans text-caption font-medium text-primary-foreground',
]);

/** The top-right corner slot, for a countdown or a price note. */
export const planCardCornerRecipe = cva([uiMarker, 'absolute right-4 top-4']);

export type PlanCardRecipeProps = VariantProps<typeof planCardRecipe>;
